/* eslint-disable @typescript-eslint/no-explicit-any */
import merge from "lodash/merge";
import isPlainObject from "lodash/isPlainObject";
import { makeStyles, Theme } from "@material-ui/core/styles";

import { pseudoClasses, stylesMap } from "../../shared/theme/styles";
import { config } from "../config";
import { WithStylesOptions } from "@material-ui/styles";

export const recursivelyApplyImportantToProperty = (
  property: string,
  styles: Record<string, any>,
): Record<string, any> =>
  Object.keys(styles).reduce(
    (result, key) => {
      const value = styles[key];

      if (isPlainObject(value)) {
        return {
          ...result,
          [key]: recursivelyApplyImportantToProperty(property, value),
        };
      }

      if (key === property) {
        return {
          ...result,
          [key]: [[].concat(value), "!important"],
        };
      }

      return result;
    },
    { ...styles },
  );

const buildComponentOverrides = (
  styles: Record<string, any>,
  overrides: Record<string, any> = {},
): Record<string, any> =>
  Object.keys(styles).reduce((mapped: Record<string, any>, key) => {
    const keyStyles = merge({}, styles[key], overrides[key]);

    if (key.startsWith("@media")) {
      mapped[key] = buildComponentOverrides(keyStyles);
    } else if (pseudoClasses.includes(key) || key.startsWith("@")) {
      mapped[key] = keyStyles;
    } else {
      mapped[key] = {
        ...keyStyles,
        [`.${config.baseClassName} &`]: keyStyles,
      };
    }
    return mapped;
  }, {});

export const buildOverrides = (
  muiTheme: Theme & { overrides?: Record<string, any> },
): Record<string, any> => {
  const { overrides } = muiTheme;

  return Object.keys({ ...overrides, ...stylesMap }).reduce(
    (mapped: Record<string, any>, component) => {
      const stylesFn = (stylesMap as Record<string, any>)[component];
      const styles =
        typeof stylesFn === "function" ? stylesFn(muiTheme) : stylesFn;

      if (styles) {
        mapped[component] = buildComponentOverrides(
          styles,
          overrides?.[component],
        );
      } else {
        console.warn(
          `[NO_STYLES_FOUND] Need to import styles for ${component}`,
        );
        mapped[component] = overrides?.[component];
      }

      return mapped;
    },
    {},
  );
};

export const makeRootStyles = <T extends Record<string, any>>(
  stylesFn: ((theme: Theme) => T) | T,
  opts?: Omit<WithStylesOptions<Theme>, "withTheme">,
): (() => { [K in keyof T]: string }) => {
  type ReturnStyleMapping = () => { [K in keyof T]: string };

  const processStyles = (styles: Record<string, any>) =>
    recursivelyApplyImportantToProperty(
      "boxSizing",
      buildComponentOverrides(styles),
    );

  if (typeof stylesFn === "function") {
    return makeStyles(
      (theme: Theme) => processStyles(stylesFn(theme)),
      opts,
    ) as unknown as ReturnStyleMapping;
  }

  if (typeof stylesFn === "object" && stylesFn !== null) {
    return makeStyles(
      processStyles(stylesFn),
      opts,
    ) as unknown as ReturnStyleMapping;
  }

  throw new TypeError("stylesFn must be a function or a non-null object.");
};
